import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";

const LOGO_URL = "/vanguard-logo.png";
const KINETICO_LOGO_URL =
  "https://www.kinetico.com/media/klgnypsr/kinetico-logo-cmyk-with-white.png";

const links = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Products" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/service-areas", label: "Service Areas" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      {/* Co-brand strip: Vanguard + Kinetico Authorized Dealer */}
      <div
        className="fixed top-0 left-0 right-0 z-40 border-b border-white/5"
        style={{
          background: "rgba(4,15,20,0.85)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
        }}
        data-testid="cobrand-strip"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 h-24 lg:h-32 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center" data-testid="cobrand-vanguard">
            <img
              src={LOGO_URL}
              alt="Vanguard Water Systems"
              className="h-20 lg:h-28 w-auto shrink-0"
            />
          </Link>
          <div className="hidden sm:flex items-center gap-4 text-[9px] lg:text-[10px] uppercase tracking-[0.35em] text-[color:var(--muted)]">
            <span className="h-px w-6 lg:w-10 bg-white/20" />
            Authorized Independent Dealer
            <span className="h-px w-6 lg:w-10 bg-white/20" />
          </div>
          <a
            href="https://www.kinetico.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center"
            data-testid="cobrand-kinetico"
          >
            <img
              src={KINETICO_LOGO_URL}
              alt="Kinetico Water Systems"
              className="h-8 sm:h-12 lg:h-20 w-auto shrink-0"
            />
          </a>
        </div>
      </div>

      <motion.header
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1], delay: 0.2 }}
        className="fixed left-0 right-0 z-50"
        style={{ top: "var(--cobrand-h, 96px)" }}
        data-testid="site-nav"
      >
        <div
          className="mx-auto max-w-[1400px] px-6 lg:px-10"
          style={{
            transition:
              "padding-top 400ms cubic-bezier(0.2,0.7,0.2,1), padding-bottom 400ms cubic-bezier(0.2,0.7,0.2,1)",
            paddingTop: scrolled ? 8 : 16,
            paddingBottom: scrolled ? 8 : 16,
          }}
        >
          <div
            className="flex items-center justify-between rounded-full px-5 lg:px-6"
            style={{
              background: scrolled ? "rgba(8,26,26,0.72)" : "rgba(8,26,26,0.35)",
              backdropFilter: "blur(18px)",
              WebkitBackdropFilter: "blur(18px)",
              border: "1px solid rgba(255,255,255,0.06)",
              transition: "background 400ms ease",
              paddingTop: 10,
              paddingBottom: 10,
            }}
          >
            <div className="w-8" aria-hidden="true" />

            <nav className="hidden lg:flex items-center gap-8">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
                  className={({ isActive }) =>
                    `text-[13px] font-body tracking-wide link-underline ${
                      isActive ? "text-[color:var(--blue)]" : "text-[color:var(--text)]/80 hover:text-white"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-3">
              <a
                href="tel:+14357017171"
                className="hidden md:inline-flex items-center gap-2 text-[12px] tracking-widest uppercase text-[color:var(--muted)] hover:text-white transition-colors"
                data-testid="nav-phone"
              >
                <Phone className="h-3.5 w-3.5" /> 435 . 701 . 7171
              </a>
              <Link
                to="/contact"
                className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] font-medium tracking-wide"
                style={{ background: "var(--blue)", color: "#041a12" }}
                data-testid="nav-quote-cta"
              >
                Free Water Test
              </Link>
              <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-label="Toggle menu"
                data-testid="nav-mobile-toggle"
                className="lg:hidden p-2 rounded-full border border-white/10 hover:border-white/30 transition-colors"
              >
                {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 lg:hidden bg-[color:var(--bg)]/95 backdrop-blur-xl pt-28 px-8"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-6">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.06 * i, ease: [0.2, 0.7, 0.2, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(" ", "-")}`}
                    className="font-display text-4xl text-white"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link to="/contact" className="btn-primary mt-6 w-fit" data-testid="mobile-nav-quote">
                Free Water Test
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
